<template>
  <nav class="h-screen overflow-y-auto space-y-4">
    <!-- Dashboard -->
    <router-link to="/dashboard" class="flex items-center space-x-2 p-3 rounded-lg" active-class="bg-blue-500">
      <font-awesome-icon :icon="['fas', 'layer-group']" /> <span>Dashboard</span>
    </router-link>
    <router-link to="/billing" class="flex items-center space-x-2 p-2 rounded-lg" active-class="bg-blue-500">
      <font-awesome-icon :icon="['fas', 'file-invoice-dollar']" /> <span>Billing</span>
    </router-link>
  
      <!-- Compute Section -->
      <div>
        <button @click="toggleSection('compute')" class="flex justify-between items-center w-full p-3 font-semibold">
          <span>Compute</span> 
          <font-awesome-icon 
            :icon="['fas', 'chevron-right']" 
            class="transition-transform duration-300 ease-in-out"
            :class="{'rotate-90': isOpen.compute}"
          />
        </button>
        <div v-if="isOpen.compute" class="pl-4 space-y-2">
          <router-link to="/servers" class="flex items-center space-x-2 p-2 rounded-lg" active-class="bg-blue-500">
            <font-awesome-icon :icon="['fas', 'server']" /> <span>Servers</span>
          </router-link>
          <router-link to="/kubernetes" class="flex items-center space-x-2 p-2 rounded-lg" active-class="bg-blue-500">
            <font-awesome-icon :icon="['fas', 'cubes']" /> <span>Kubernetes</span>
          </router-link>
        </div>
      </div>
  
      <!-- Networking Section -->
      <div>
        <button @click="toggleSection('network')" class="flex justify-between items-center w-full p-3 font-semibold">
          <span>Networking</span> 
          <font-awesome-icon 
            :icon="['fas', 'chevron-right']" 
            class="transition-transform duration-300 ease-in-out"
            :class="{'rotate-90': isOpen.network}"
          />
        </button>
        <div v-if="isOpen.network" class="pl-4 space-y-2">
          <router-link to="/load-balancers" class="flex items-center space-x-2 p-2 rounded-lg" active-class="bg-blue-500">
            <font-awesome-icon :icon="['fas', 'bars-progress']" /> <span>Load Balancers</span>
          </router-link>
          <router-link to="/private-networks" class="flex items-center space-x-2 p-2 rounded-lg" active-class="bg-blue-500">
            <font-awesome-icon :icon="['fas', 'globe']" /> <span>Private Networks</span>
          </router-link>
          <router-link to="/network" class="flex items-center space-x-2 p-2 rounded-lg" active-class="bg-blue-500">
            <font-awesome-icon :icon="['fas', 'ethernet']" /> <span>BGP</span>
          </router-link>
          <router-link to="/cdn" class="flex items-center space-x-2 p-2 rounded-lg" active-class="bg-blue-500">
            <font-awesome-icon :icon="['fas', 'network-wired']" /> <span>CDN</span>
          </router-link>
        </div>
      </div>
  
      <!-- Storage Section -->
      <div>
        <button @click="toggleSection('storage')" class="flex justify-between items-center w-full p-3 font-semibold">
          <span>Storage</span> 
          <font-awesome-icon 
            :icon="['fas', 'chevron-right']" 
            class="transition-transform duration-300 ease-in-out"
            :class="{'rotate-90': isOpen.storage}"
          />
        </button>
        <div v-if="isOpen.storage" class="pl-4 space-y-2">
          <router-link to="/storage" class="flex items-center space-x-2 p-2 rounded-lg" active-class="bg-blue-500">
            <font-awesome-icon :icon="['fas', 'hdd']" /> <span>Storage</span>
          </router-link>
          <router-link to="/databases" class="flex items-center space-x-2 p-2 rounded-lg" active-class="bg-blue-500">
            <font-awesome-icon :icon="['fas', 'database']" /> <span>Managed Databases</span>
          </router-link>
        </div>
      </div>
  
      <!-- Other Section -->
      <div>
        <button @click="toggleSection('other')" class="flex justify-between items-center w-full p-3 font-semibold">
          <span>Support</span> 
          <font-awesome-icon 
            :icon="['fas', 'chevron-right']" 
            class="transition-transform duration-300 ease-in-out"
            :class="{'rotate-90': isOpen.other}"
          />
        </button>
        <div v-if="isOpen.other" class="pl-4 space-y-2">
          <router-link to="/support" class="flex items-center space-x-2 p-2 rounded-lg" active-class="bg-blue-500">
            <font-awesome-icon :icon="['fas', 'ticket-simple']" /> <span>Support</span>
          </router-link>
          <router-link to="/affiliates" class="flex items-center space-x-2 p-2 rounded-lg" active-class="bg-blue-500">
            <font-awesome-icon :icon="['fas', 'user-group']" /> <span>Affiliates</span>
          </router-link>
          <router-link to="/settings" class="flex items-center space-x-2 p-2 rounded-lg" active-class="bg-blue-500">
            <font-awesome-icon :icon="['fas', 'gear']" /> <span>Settings</span>
          </router-link>
        </div>
      </div>
  
      <!-- Admin Links (Only visible if user is admin) -->
      <template v-if="isAdmin">
        <hr class="border-gray-600" />
        <router-link to="/admin/dashboard" class="flex items-center space-x-2 p-3 rounded-lg" active-class="bg-red-500">
          <font-awesome-icon :icon="['fas', 'chart-line']" /> <span>Admin Dashboard</span>
        </router-link>
      </template>
    </nav>
  </template>
  
  <script>
  import axios from 'axios';
  
  export default {
    name: 'NavBar',
    data() {
      return {
        userRole: null,
        isOpen: JSON.parse(localStorage.getItem('navState')) || {
          compute: true,
          network: false,
          storage: false,
          other: false
        }
      };
    },
    computed: {
      isAdmin() {
        return ['admin', 'noc', 'billing', 'support'].includes(this.userRole);
      }
    },
    methods: {
      toggleSection(section) {
        this.isOpen[section] = !this.isOpen[section];
        localStorage.setItem('navState', JSON.stringify(this.isOpen)); // Save to localStorage
      }
    },
    async created() {
      try {
        const response = await axios.get('https://api.syniac.com:3000/api/auth/session', { withCredentials: true });
        if (response.data.loggedIn) {
          this.userRole = response.data.user.role;
        }
      } catch (error) {
        console.error('Error fetching session:', error);
      }
    }
  };
  </script>
